/**
 * Render Markdown to PDF (Production Quality)
 *
 * Uses html2pdf for proper Unicode support and clean rendering.
 * Matches the markdown browser rendering exactly.
 */

import html2pdf from "html2pdf.js";

import { markdownToHtml } from "./markdownConverter";
import { getPdfFullStylesheet, PDF_PAGEBREAK_CONFIG, PDF_CONTAINER_CLASS } from "./pdfStyles";

export interface PdfOptions {
  title?: string;
  author?: string;
  subject?: string;
  keywords?: string;
  brandFontFamily?: string;
  brandCodeFontFamily?: string;
}

/**
 * Render markdown to PDF with full formatting preservation
 *
 * Production-quality PDF generation that preserves:
 * - Bold and italic text
 * - Headings with appropriate sizing
 * - Bullet and numbered lists with proper symbols
 * - Unicode characters (arrows, special symbols)
 * - Proper line wrapping and spacing
 * - Professional typography
 *
 * @param markdown - Markdown content to render
 * @param options - PDF metadata options
 * @returns jsPDF document instance
 */
export async function renderMarkdownToPdf(
  markdown: string,
  options: PdfOptions = {},
): Promise<any> {
  // Convert markdown to HTML
  let html = markdownToHtml(markdown);

  // Wrap headings with their following content to prevent orphaned headings at page breaks
  // This wraps each heading and the content until the next heading in a section div
  html = html.replace(
    /(<h([1-6])[^>]*>[\s\S]*?<\/h\2>)([\s\S]*?)(?=<h[1-6]|$)/gi,
    '<div class="section-keep-together">$1$3</div>',
  );

  // Create a styled container for PDF rendering using shared styles
  // IMPORTANT: Content is wrapped in a scoped container class to prevent
  // PDF styles from leaking into the main document during html2pdf.js rendering
  const styledHtml = `
    <html>
      <head>
        <style>
          ${getPdfFullStylesheet({
            fontFamily: options.brandFontFamily,
            codeFontFamily: options.brandCodeFontFamily,
          })}
        </style>
      </head>
      <body>
        <div class="${PDF_CONTAINER_CLASS}">
          ${html}
        </div>
      </body>
    </html>
  `;

  // Configure html2pdf options
  const opt = {
    margin: 20,
    filename: options.title || "document.pdf",
    image: { type: "jpeg" as const, quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      letterRendering: true,
    },
    jsPDF: {
      unit: "mm" as const,
      format: "a4" as const,
      orientation: "portrait" as const,
    },
    // Prevent word/element splitting across page boundaries
    pagebreak: PDF_PAGEBREAK_CONFIG,
  };

  // Generate PDF and return the jsPDF instance
  const pdfResult = await html2pdf().set(opt).from(styledHtml).toPdf().get("pdf");

  // Set document properties if provided
  if (options.title) {
    pdfResult.setProperties({ title: options.title });
  }
  if (options.author) {
    pdfResult.setProperties({ author: options.author });
  }
  if (options.subject) {
    pdfResult.setProperties({ subject: options.subject });
  }
  if (options.keywords) {
    pdfResult.setProperties({ keywords: options.keywords });
  }

  return pdfResult;
}

/**
 * Backward compatibility wrapper
 * @deprecated Use renderMarkdownToPdf instead
 */
export async function renderMarkdownToPdfSimple(
  _doc: any,
  markdown: string,
  _startX: number,
  _startY: number,
  _maxWidth: number,
): Promise<number> {
  // This legacy function is deprecated
  // Just render the markdown and return a dummy position
  await renderMarkdownToPdf(markdown);

  return 100; // Return a dummy Y position for compatibility
}
